import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PatientSidebar from './PatientSidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faVideo, faCalendar, faHistory, faPlus, faUsers, faMessage,
    faEye, faClock, faExclamationTriangle, faCheckCircle, faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { useBaseUrl } from '../utils/utils';
import localForage from 'localforage';

interface TelehealthSession {
    id: string;
    title: string;
    description?: string;
    scheduled_at: string;
    status: 'scheduled' | 'active' | 'completed' | 'cancelled';
    participants: string[];
    type: 'consultation' | 'follow-up' | 'emergency' | 'collaboration';
    appointment_method: 'telehealth' | 'hybrid';
    duration: number; // in minutes
    provider_name: string;
    clinic_name: string;
    collaboration_id?: string;
    collaboration_name?: string;
    task_name?: string;
    telehealth_link?: string;
    notes?: string;
    created_at: string;
    updated_at: string;
}

interface Contact {
    id: string;
    name: string;
    role: string;
    specialty?: string;
    avatar: string;
    status: 'online' | 'offline' | 'away';
    last_seen: string;
    clinic_name: string;
    email: string;
    phone: string;
    is_collaboration_partner: boolean;
    collaboration_id?: string;
    collaboration_name?: string;
}

const PatientTelehealthCenter: React.FC = () => {
    const navigate = useNavigate();
    const baseUrl = useBaseUrl();
    
    const [upcomingSessions, setUpcomingSessions] = useState<TelehealthSession[]>([]);
    const [recentSessions, setRecentSessions] = useState<TelehealthSession[]>([]);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [patientId, setPatientId] = useState<string | null>(null);

    useEffect(() => {
        document.title = "Telehealth Center - Uzima Healthcare";
        initializeTelehealth();
    }, []);

    const initializeTelehealth = async () => {
        try {
            setLoading(true);
            
            // Get patient ID from localForage
            const storedPatientId = await localForage.getItem("patient_id");
            if (!storedPatientId) {
                throw new Error('Patient ID not found');
            }
            setPatientId(storedPatientId as string);
            
            // Fetch telehealth data from backend
            await Promise.all([
                fetchUpcomingSessions(storedPatientId as string),
                fetchRecentSessions(storedPatientId as string),
                fetchContacts(storedPatientId as string)
            ]);
            
        } catch (error) {
            console.error('Error initializing telehealth:', error);
            setError('Failed to load telehealth data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchUpcomingSessions = async (patientId: string) => {
        try {
            const response = await fetch(`${baseUrl}collaborations/api/patient/telehealth/upcoming-sessions/?patient_id=${patientId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${await localForage.getItem('access_token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setUpcomingSessions(data.sessions || []);

        } catch (error) {
            console.error('Error fetching upcoming sessions:', error);
        }
    };

    const fetchRecentSessions = async (patientId: string) => {
        try {
            const response = await fetch(`${baseUrl}collaborations/api/patient/telehealth/recent-sessions/?patient_id=${patientId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${await localForage.getItem('access_token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setRecentSessions(data.sessions || []);

        } catch (error) {
            console.error('Error fetching recent sessions:', error);
        }
    };

    const fetchContacts = async (patientId: string) => {
        try {
            const response = await fetch(`${baseUrl}collaborations/api/patient/telehealth/contacts/?patient_id=${patientId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${await localForage.getItem('access_token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setContacts(data.contacts || []);

        } catch (error) {
            console.error('Error fetching contacts:', error);
        }
    };

    const joinSession = (sessionId: string) => {
        navigate(`/patient/telehealth/session/${sessionId}`);
    };

    const scheduleSession = () => {
        navigate('/patient/telehealth/schedule');
    };

    const startChat = (contactId: string) => {
        navigate(`/patient/telehealth/chat/${contactId}`);
    };

    const viewSessionHistory = () => {
        navigate('/patient/telehealth/history');
    };

    const getStatusBadge = (status: string) => {
        const statusConfig = {
            scheduled: { color: 'warning', icon: faClock, text: 'Scheduled' },
            active: { color: 'success', icon: faCheckCircle, text: 'Active' },
            completed: { color: 'info', icon: faCheckCircle, text: 'Completed' },
            cancelled: { color: 'danger', icon: faTimesCircle, text: 'Cancelled' }
        };
        
        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.scheduled;
        
        return (
            <span className={`badge bg-${config.color} bg-opacity-10 text-${config.color} fw-bold`}>
                <FontAwesomeIcon icon={config.icon} className="me-1" size="sm" />
                {config.text}
            </span>
        );
    };

    const getTypeBadge = (type: string) => {
        const typeConfig = {
            consultation: { color: 'primary', text: 'Consultation' },
            'follow-up': { color: 'success', text: 'Follow-up' },
            emergency: { color: 'danger', text: 'Emergency' },
            collaboration: { color: 'warning', text: 'Collaboration' }
        };
        
        const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.consultation;
        
        return (
            <span className={`badge bg-${config.color} bg-opacity-10 text-${config.color} fw-bold`}>
                {config.text}
            </span>
        );
    };

    const refreshData = () => {
        if (patientId) {
            initializeTelehealth();
        }
    };

    if (loading) {
        return (
            <div className="d-flex" style={{ minHeight: '100vh', background: '#fafbfc' }}>
                <PatientSidebar />
                <main className="flex-grow-1 px-4 py-4" style={{ maxWidth: 1400, margin: '0 auto' }}>
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-3">Loading telehealth center...</div>
                    </div>
                </main>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex" style={{ minHeight: '100vh', background: '#fafbfc' }}>
                <PatientSidebar />
                <main className="flex-grow-1 px-4 py-4" style={{ maxWidth: 1400, margin: '0 auto' }}>
                    <div className="text-center py-5">
                        <div className="alert alert-danger" role="alert">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            {error}
                        </div>
                        <button className="btn btn-primary mt-3" onClick={refreshData}>
                            <FontAwesomeIcon icon={faClock} className="me-2" />
                            Try Again
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="d-flex" style={{ minHeight: '100vh', background: '#fafbfc' }}>
            <PatientSidebar />
            <main className="flex-grow-1 px-4 py-4" style={{ maxWidth: 1400, margin: '0 auto' }}>
                {/* Header */}
                <div className="mb-4">
                    <h2 className="fw-bold mb-1">
                        <FontAwesomeIcon icon={faVideo} className="me-2 text-primary" />
                        Telehealth Center
                    </h2>
                    <div className="text-muted fs-5">Connect with healthcare providers remotely</div>
                </div>

                {/* Quick Actions */}
                <div className="row g-3 mb-4">
                    <div className="col-md-3">
                        <button 
                            className="card card-animate text-center border-0 shadow-sm py-4 h-100 text-decoration-none bg-white w-100"
                            onClick={scheduleSession}
                        >
                            <div className="mx-auto mb-3 rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 56, height: 56 }}>
                                <FontAwesomeIcon icon={faCalendar} size="lg" className="text-primary" />
                            </div>
                            <div className="fw-bold fs-6 text-dark">Schedule Session</div>
                            <div className="text-muted small">Book a telehealth appointment</div>
                        </button>
                    </div>
                    <div className="col-md-3">
                        <button 
                            className="card card-animate text-center border-0 shadow-sm py-4 h-100 text-decoration-none bg-white w-100"
                            onClick={() => navigate('/patient/telehealth/chat')}
                        >
                            <div className="mx-auto mb-3 rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 56, height: 56 }}>
                                <FontAwesomeIcon icon={faMessage} size="lg" className="text-success" />
                            </div>
                            <div className="fw-bold fs-6 text-dark">Secure Chat</div>
                            <div className="text-muted small">Message healthcare providers</div>
                        </button>
                    </div>
                    <div className="col-md-3">
                        <button 
                            className="card card-animate text-center border-0 shadow-sm py-4 h-100 text-decoration-none bg-white w-100"
                            onClick={viewSessionHistory}
                        >
                            <div className="mx-auto mb-3 rounded-circle bg-info bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 56, height: 56 }}>
                                <FontAwesomeIcon icon={faHistory} size="lg" className="text-info" />
                            </div>
                            <div className="fw-bold fs-6 text-dark">Session History</div>
                            <div className="text-muted small">View past consultations</div>
                        </button>
                    </div>
                    <div className="col-md-3">
                        <button 
                            className="card card-animate text-center border-0 shadow-sm py-4 h-100 text-decoration-none bg-white w-100"
                            onClick={() => navigate('/patient/telehealth/contacts')}
                        >
                            <div className="mx-auto mb-3 rounded-circle bg-warning bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 56, height: 56 }}>
                                <FontAwesomeIcon icon={faUsers} size="lg" className="text-warning" />
                            </div>
                            <div className="fw-bold fs-6 text-dark">My Contacts</div>
                            <div className="text-muted small">Manage healthcare contacts</div>
                        </button>
                    </div>
                </div>

                <div className="row g-4">
                    {/* Upcoming Sessions */}
                    <div className="col-lg-8">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0">
                                    <FontAwesomeIcon icon={faCalendar} className="me-2" />
                                    Upcoming Sessions
                                </h5>
                            </div>
                            <div className="card-body">
                                {upcomingSessions.length > 0 ? (
                                    upcomingSessions.map((session) => (
                                        <div key={session.id} className="session-item d-flex align-items-center justify-content-between p-3 border-bottom">
                                            <div className="flex-grow-1">
                                                <h6 className="mb-1">{session.title}</h6>
                                                <div className="text-muted small mb-2">
                                                    <FontAwesomeIcon icon={faClock} className="me-1" />
                                                    {new Date(session.scheduled_at).toLocaleString()}
                                                </div>
                                                <div className="text-muted small mb-2">
                                                    <FontAwesomeIcon icon={faUsers} className="me-1" />
                                                    {session.participants.join(', ')}
                                                </div>
                                                <div className="d-flex gap-2 mb-2">
                                                    {getTypeBadge(session.type)}
                                                    {getStatusBadge(session.status)}
                                                    {session.appointment_method === 'telehealth' && (
                                                        <span className="badge bg-info bg-opacity-10 text-info fw-bold">
                                                            Telehealth
                                                        </span>
                                                    )}
                                                </div>
                                                {session.type === 'collaboration' && session.collaboration_name && (
                                                    <div className="text-info small">
                                                        <FontAwesomeIcon icon={faUsers} className="me-1" />
                                                        Collaboration: {session.collaboration_name}
                                                        {session.task_name && (
                                                            <span className="ms-2">• Task: {session.task_name}</span>
                                                        )}
                                                    </div>
                                                )}
                                                {session.description && (
                                                    <div className="text-muted small mt-2">
                                                        {session.description}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button 
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => joinSession(session.id)}
                                                >
                                                    <FontAwesomeIcon icon={faVideo} className="me-1" />
                                                    Join
                                                </button>
                                                <button className="btn btn-outline-secondary btn-sm">
                                                    <FontAwesomeIcon icon={faEye} className="me-1" />
                                                    Details
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-4">
                                        <FontAwesomeIcon icon={faCalendar} size="2x" className="text-muted mb-2" />
                                        <p className="text-muted">No upcoming sessions</p>
                                        <button className="btn btn-primary" onClick={scheduleSession}>
                                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                                            Schedule Session
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Quick Stats & Contacts */}
                    <div className="col-lg-4">
                        <div className="row g-3">
                            {/* Quick Stats */}
                            <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center">
                                        <h4 className="fw-bold text-primary mb-2">{upcomingSessions.length}</h4>
                                        <div className="text-muted">Upcoming Sessions</div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center">
                                        <h4 className="fw-bold text-success mb-2">{recentSessions.length}</h4>
                                        <div className="text-muted">Recent Sessions</div>
                                    </div>
                                </div>
                            </div>

                            {/* Healthcare Contacts */}
                            <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header">
                                        <h6 className="mb-0">
                                            <FontAwesomeIcon icon={faUsers} className="me-2" />
                                            Healthcare Contacts
                                        </h6>
                                    </div>
                                    <div className="card-body p-0">
                                        {contacts.slice(0, 3).map((contact) => (
                                            <div key={contact.id} className="contact-item d-flex align-items-center p-3 border-bottom">
                                                <img 
                                                    src={contact.avatar} 
                                                    alt={contact.name}
                                                    className="rounded-circle me-3"
                                                    style={{ width: 40, height: 40 }}
                                                />
                                                <div className="flex-grow-1">
                                                    <div className="fw-medium">{contact.name}</div>
                                                    <div className="text-muted small">{contact.role}</div>
                                                    <div className="text-muted small">
                                                        <span className={`status-dot ${contact.status}`}></span>
                                                        {contact.status}
                                                    </div>
                                                    {contact.is_collaboration_partner && (
                                                        <div className="text-info small">
                                                            <FontAwesomeIcon icon={faUsers} className="me-1" />
                                                            Collaboration Partner
                                                        </div>
                                                    )}
                                                </div>
                                                <button 
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => startChat(contact.id)}
                                                >
                                                    <FontAwesomeIcon icon={faMessage} />
                                                </button>
                                            </div>
                                        ))}
                                        <div className="p-3 text-center">
                                            <button 
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={() => navigate('/patient/telehealth/contacts')}
                                            >
                                                View All Contacts
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <style>{`
                .session-item:last-child {
                    border-bottom: none !important;
                }
                
                .status-dot {
                    display: inline-block;
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    margin-right: 5px;
                }
                
                .status-dot.online { background-color: #28a745; }
                .status-dot.offline { background-color: #6c757d; }
                .status-dot.away { background-color: #ffc107; }
                
                .contact-item:last-child {
                    border-bottom: none !important;
                }
            `}</style>
        </div>
    );
};

export default PatientTelehealthCenter;
